import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Check, ChevronsUpDown, ChevronDown, ChevronUp, Clock, MapPin, CalendarIcon, CopyPlus } from "lucide-react";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useWorkflowsList } from "@/hooks/workflow/useWorkflowsList";
import { useTimezones } from "@/hooks/data/useTimezones";
import { usePatientGroups } from "@/hooks/activity/usePatientGroups";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar } from "@/components/ui/calendar";
import { format, eachDayOfInterval } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { activityService } from "@/services/features/ActivityService";
import type { CreateActivityPayload } from "@/services/features/ActivityService";
import { appointmentService } from "@/services";
import { parseISO } from "date-fns";

// Schema and type definitions
const formSchema = z.object({
  activityName: z.string().min(1, "Activity name is required"),
  patientGroup: z.string().min(1, "Patient group is required"),
  initialWorkflow: z.string().min(1, "Workflow is required"),
  appointmentSlots: z.array(z.string()).optional(),
});

type FormData = z.infer<typeof formSchema>;

interface TimeSlot {
  id: string;
  fromTime: string;
  toTime: string;
  timezone: string;
  slotDurationMinutes: string;
  error?: string;
  mapperIds?: number[]; // Store mapper IDs for deletion
  visibilityWindowId?: number | null; // Store visibility window ID for updates
}

interface DaySlots {
  enabled: boolean;
  slots: TimeSlot[];
}

interface ActivityFormProps {
  initialData?: Partial<FormData>;
  isEdit?: boolean;
  activityId?: number;
  isDuplicate?: boolean;
  sourceActivityId?: number;
}

const ActivityForm = ({ initialData, isEdit = false, activityId, isDuplicate = false, sourceActivityId }: ActivityFormProps) => {
  const navigate = useNavigate();
  const { token, hasuraToken, user } = useAuth();
  
  // Fetch real data
  const { patientGroups, loading: patientGroupsLoading, error: patientGroupsError } = usePatientGroups();
  const { workflows, loading: workflowsLoading, error: workflowsError } = useWorkflowsList();
  const { timezones, loading: timezonesLoading, error: timezonesError } = useTimezones();
  
  const [selectedTimezone, setSelectedTimezone] = useState("US/Pacific");
  const [fromDate, setFromDate] = useState<Date | undefined>();
  const [toDate, setToDate] = useState<Date | undefined>();
  const [dateError, setDateError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeletingSlot, setIsDeletingSlot] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState<{ day: string; slot: TimeSlot } | null>(null);
  const [showRemoveAllSlotsDialog, setShowRemoveAllSlotsDialog] = useState<{ day: string; slotId: string } | null>(null);
  const [activityConfigId, setActivityConfigId] = useState<number | undefined>();
  const [activityIdFromConfig, setActivityIdFromConfig] = useState<number | undefined>();
  
  const [appointmentSlots, setAppointmentSlots] = useState<Record<string, DaySlots>>({
    Monday: { enabled: false, slots: [{ id: "1", fromTime: "", toTime: "", timezone: "US/Pacific", slotDurationMinutes: "" }] },
    Tuesday: { enabled: false, slots: [{ id: "3", fromTime: "", toTime: "", timezone: "US/Pacific", slotDurationMinutes: "" }] },
    Wednesday: { enabled: false, slots: [{ id: "4", fromTime: "", toTime: "", timezone: "US/Pacific", slotDurationMinutes: "" }] },
    Thursday: { enabled: false, slots: [{ id: "5", fromTime: "", toTime: "", timezone: "US/Pacific", slotDurationMinutes: "" }] },
    Friday: { enabled: false, slots: [{ id: "6", fromTime: "", toTime: "", timezone: "US/Pacific", slotDurationMinutes: "" }] },
    Saturday: { enabled: false, slots: [{ id: "7", fromTime: "", toTime: "", timezone: "US/Pacific", slotDurationMinutes: "" }] },
    Sunday: { enabled: false, slots: [{ id: "8", fromTime: "", toTime: "", timezone: "US/Pacific", slotDurationMinutes: "" }] }
  });
  
  const [daysInRange, setDaysInRange] = useState<string[]>([]);

  // Track initial state for change detection
  const [initialFormState, setInitialFormState] = useState<{
    formData: Partial<FormData>;
    appointmentSlots: Record<string, DaySlots>;
    fromDate: Date | undefined;
    toDate: Date | undefined;
    timezone: string;
  } | null>(null);

  const [collapsedDays, setCollapsedDays] = useState<Record<string, boolean>>({});

  // Auto-detect timezone (simulated)
  const detectedTimezone = "US/Pacific";

  // Generate time options
  const hourOptions = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1)
  }));

  const minuteOptions = [
    { value: '00', label: '00' },
    { value: '15', label: '15' },
    { value: '30', label: '30' },
    { value: '45', label: '45' }
  ];

  const periodOptions = [
    { value: 'AM', label: 'AM' },
    { value: 'PM', label: 'PM' }
  ];

  // Helper function to parse time string into components
  // Returns default values ('00' for minutes) to ensure fields are never empty
  const parseTimeString = (timeStr: string) => {
    if (!timeStr) return { hour: '', minute: '00', period: 'AM' };
    const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return { hour: '', minute: '00', period: 'AM' };
    return {
      hour: match[1],
      minute: match[2] || '00',
      period: match[3].toUpperCase()
    };
  };

  // Helper function to construct time string from components
  // Ensures minute is never empty by defaulting to '00'
  const constructTimeString = (hour: string, minute: string, period: string) => {
    if (!hour || !period) return '';
    // Auto-correct empty minute to '00'
    const safeMinute = minute || '00';
    return `${hour}:${safeMinute} ${period}`;
  };

  // Calculate days in range when dates change
  const calculateDaysInRange = (start: Date | undefined, end: Date | undefined) => {
    if (!start || !end) {
      setDaysInRange([]);
      return;
    }
    
    const days = eachDayOfInterval({ start, end });
    const dayNames = days.map(date => {
      const dayIndex = date.getDay();
      return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayIndex];
    });
    
    // Get unique days
    const uniqueDays = Array.from(new Set(dayNames));
    setDaysInRange(uniqueDays);
  };

  // Update days in range when dates change
  React.useEffect(() => {
    calculateDaysInRange(fromDate, toDate);
  }, [fromDate, toDate]);

  // Fetch and populate activity config data when editing or duplicating
  React.useEffect(() => {
    const targetActivityId = isEdit ? activityId : isDuplicate ? sourceActivityId : null;
    console.log('Activity useEffect triggered:', { isEdit, isDuplicate, targetActivityId, hasToken: !!token, userId: user?.id });
    
    if ((isEdit || isDuplicate) && targetActivityId && token && user?.id) {
      const fetchActivityConfig = async () => {
        try {
          console.log('Fetching activity config for activity:', targetActivityId);
          
          const config = await activityService.getActivityConfig(
            parseInt(user.id),
            targetActivityId,
            token
          );
          console.log('Activity config response:', config);
          
          if (config) {
            // Helper function to convert HH:MM:SS to 12-hour format
            const convert24To12Hour = (time24: string): string => {
              const [hours, minutes] = time24.split(':');
              const hour = parseInt(hours);
              const ampm = hour >= 12 ? 'PM' : 'AM';
              const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
              return `${hour12}:${minutes} ${ampm}`;
            };

            // Map numeric dayOfWeek to day names
            const dayNumberToName: Record<number, string> = {
              1: 'Monday',
              2: 'Tuesday',
              3: 'Wednesday',
              4: 'Thursday',
              5: 'Friday',
              6: 'Saturday',
              7: 'Sunday'
            };

            // Set dates
            if (config.allowedFromDate) {
              setFromDate(parseISO(config.allowedFromDate));
            }
            if (config.allowedToDate) {
              setToDate(parseISO(config.allowedToDate));
            }
            
            // Set timezone
            if (config.timezone) {
              setSelectedTimezone(config.timezone);
            }

            // Transform visibilityWindows to appointment slots format
            const newAppointmentSlots: Record<string, DaySlots> = {
              Monday: { enabled: false, slots: [{ id: "Monday-1", fromTime: "", toTime: "", timezone: config.timezone || selectedTimezone, slotDurationMinutes: "" }] },
              Tuesday: { enabled: false, slots: [{ id: "Tuesday-1", fromTime: "", toTime: "", timezone: config.timezone || selectedTimezone, slotDurationMinutes: "" }] },
              Wednesday: { enabled: false, slots: [{ id: "Wednesday-1", fromTime: "", toTime: "", timezone: config.timezone || selectedTimezone, slotDurationMinutes: "" }] },
              Thursday: { enabled: false, slots: [{ id: "Thursday-1", fromTime: "", toTime: "", timezone: config.timezone || selectedTimezone, slotDurationMinutes: "" }] },
              Friday: { enabled: false, slots: [{ id: "Friday-1", fromTime: "", toTime: "", timezone: config.timezone || selectedTimezone, slotDurationMinutes: "" }] },
              Saturday: { enabled: false, slots: [{ id: "Saturday-1", fromTime: "", toTime: "", timezone: config.timezone || selectedTimezone, slotDurationMinutes: "" }] },
              Sunday: { enabled: false, slots: [{ id: "Sunday-1", fromTime: "", toTime: "", timezone: config.timezone || selectedTimezone, slotDurationMinutes: "" }] }
            };

            // Group visibilityWindows by day
            const windowsByDay: Record<string, typeof config.visibilityWindows> = {};
            
            config.visibilityWindows?.forEach(window => {
              const dayName = dayNumberToName[window.dayOfWeek];
              if (dayName) {
                if (!windowsByDay[dayName]) {
                  windowsByDay[dayName] = [];
                }
                windowsByDay[dayName].push(window);
              }
            });

            // Store activityConfigId for update payload - only for edit mode, not duplicate
            if (isEdit) {
              if (config.activityConfigId) {
                setActivityConfigId(config.activityConfigId);
              }
              if (config.activityId) {
                setActivityIdFromConfig(config.activityId);
              }
            }

            // Convert to TimeSlot format - for duplicate, don't preserve visibilityWindowId
            Object.entries(windowsByDay).forEach(([day, windows]) => {
              if (windows.length > 0) {
                const slots: TimeSlot[] = windows.map((window, index) => ({
                  id: `${day}-${index}`,
                  fromTime: convert24To12Hour(window.fromTime),
                  toTime: convert24To12Hour(window.toTime),
                  timezone: config.timezone || selectedTimezone,
                  slotDurationMinutes: "", // Will be auto-calculated
                  visibilityWindowId: isEdit ? window.visibilityWindowId : null // Only preserve for edit mode
                }));

                newAppointmentSlots[day] = {
                  enabled: true,
                  slots
                };
              }
            });

            console.log('Setting appointment slots from config:', newAppointmentSlots);
            setAppointmentSlots(newAppointmentSlots);
            
            // Store initial state after loading data - only for edit mode
            if (isEdit) {
              setInitialFormState({
                formData: initialData || {},
                appointmentSlots: newAppointmentSlots,
                fromDate: config.allowedFromDate ? parseISO(config.allowedFromDate) : undefined,
                toDate: config.allowedToDate ? parseISO(config.allowedToDate) : undefined,
                timezone: config.timezone || selectedTimezone
              });
            }
            
            toast.success(isDuplicate ? 'Activity data loaded for duplication' : 'Activity data loaded successfully');
          }
        } catch (error) {
          console.error('Error fetching activity config:', error);
          toast.error('Failed to load activity data');
        }
      };

      fetchActivityConfig();
    }
  }, [isEdit, isDuplicate, activityId, sourceActivityId, token, user?.id, selectedTimezone]);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      activityName: initialData?.activityName || "",
      patientGroup: initialData?.patientGroup || "",
      initialWorkflow: initialData?.initialWorkflow || "",
      appointmentSlots: [],
    },
  });

  // Watch form values for validation
  const watchedActivityName = form.watch("activityName");
  const watchedPatientGroup = form.watch("patientGroup");
  const watchedInitialWorkflow = form.watch("initialWorkflow");

  // Helper to validate time string has valid hour and minute
  const isValidTimeString = (timeStr: string): boolean => {
    if (!timeStr) return false;
    const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return false;
    const hour = parseInt(match[1]);
    const minute = match[2];
    // Ensure hour is valid (1-12) and minute is not empty
    return hour >= 1 && hour <= 12 && minute && minute.length === 2;
  };

  // Function to check if form is valid and can be saved
  const isFormValid = (): boolean => {
    // Check all required fields are filled
    const hasActivityName = !!watchedActivityName && watchedActivityName.trim().length > 0;
    const hasPatientGroup = !!watchedPatientGroup && watchedPatientGroup.trim().length > 0;
    const hasWorkflow = !!watchedInitialWorkflow && watchedInitialWorkflow.trim().length > 0;
    const hasFromDate = !!fromDate;
    const hasToDate = !!toDate;
    
    // Check if at least one day has at least one complete time slot without errors
    // Also validate that both fromTime and toTime have valid hour and minute values
    const hasValidSlot = Object.values(appointmentSlots).some(dayData => 
      dayData.enabled && dayData.slots.some(slot => 
        isValidTimeString(slot.fromTime) && isValidTimeString(slot.toTime) && !slot.error
      )
    );
    
    return hasActivityName && hasPatientGroup && hasWorkflow && hasFromDate && hasToDate && hasValidSlot;
  };

  // Function to check if any changes were made
  const hasChanges = (values: FormData): boolean => {
    if (!isEdit || !initialFormState) {
      return true; // For create mode, always allow submission
    }

    // Compare form values
    const formChanged = 
      values.activityName !== initialFormState.formData.activityName ||
      values.patientGroup !== initialFormState.formData.patientGroup ||
      values.initialWorkflow !== initialFormState.formData.initialWorkflow;

    // Compare dates
    const datesChanged = 
      fromDate?.getTime() !== initialFormState.fromDate?.getTime() ||
      toDate?.getTime() !== initialFormState.toDate?.getTime();

    // Compare timezone
    const timezoneChanged = selectedTimezone !== initialFormState.timezone;

    // Compare appointment slots (deep comparison)
    const slotsChanged = JSON.stringify(appointmentSlots) !== JSON.stringify(initialFormState.appointmentSlots);

    return formChanged || datesChanged || timezoneChanged || slotsChanged;
  };

  const onSubmit = async (values: FormData) => {
    // Check for changes in edit mode
    if (isEdit && !hasChanges(values)) {
      toast.info("No changes detected. Please modify the activity before updating.");
      return;
    }
    // Validate From Date and End Date
    if (!fromDate) {
      toast.error("From Date is required");
      return;
    }
    
    if (!toDate) {
      toast.error("End Date is required");
      return;
    }
    
    if (dateError) {
      toast.error("Please fix date validation errors");
      return;
    }
    
    // Check if at least one day has at least one complete and valid time slot
    const hasValidSlot = Object.values(appointmentSlots).some(dayData => 
      dayData.enabled && dayData.slots.some(slot => 
        isValidTimeString(slot.fromTime) && isValidTimeString(slot.toTime) && !slot.error
      )
    );
    
    if (!hasValidSlot) {
      toast.error("At least one complete time slot (with valid hours and minutes) is required");
      return;
    }
    
    // Check if any slot has errors
    const hasSlotErrors = Object.values(appointmentSlots).some(dayData =>
      dayData.enabled && dayData.slots.some(slot => slot.error)
    );
    
    if (hasSlotErrors) {
      toast.error("Please fix all time slot errors before saving");
      return;
    }

    if (!token || !user) {
      toast.error("Authentication required");
      return;
    }

    if (isEdit && !activityId) {
      toast.error("Activity ID is missing for update");
      return;
    }
    
    try {
      setIsSubmitting(true);

      // Day name to number mapping: Monday=1, Tuesday=2, etc.
      const dayToNumber: Record<string, number> = {
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
        sunday: 7
      };

      // Transform appointment slots to visibilityWindows format
      const visibilityWindows: Array<{ visibilityWindowId: number | null; dayOfWeek: number; fromTime: string; toTime: string }> = [];
      
      Object.entries(appointmentSlots)
        .filter(([_, dayData]) => dayData.enabled)
        .forEach(([day, dayData]) => {
          const dayNumber = dayToNumber[day.toLowerCase()];
          dayData.slots
            .filter(slot => slot.fromTime && slot.toTime)
            .forEach(slot => {
              visibilityWindows.push({
                visibilityWindowId: slot.visibilityWindowId ?? null, // Use existing ID or null for new slots
                dayOfWeek: dayNumber,
                fromTime: convertTo24Hour(slot.fromTime) + ":00",
                toTime: convertTo24Hour(slot.toTime) + ":00"
              });
            });
        });

      const payload: CreateActivityPayload = {
        patientGroupId: parseInt(values.patientGroup),
        initialWorkflowId: parseInt(values.initialWorkflow),
        activityName: values.activityName,
        ...(isEdit && activityIdFromConfig ? { activityId: activityIdFromConfig } : {}),
        activityConfig: {
          allowedFromDate: format(fromDate, "yyyy-MM-dd"),
          ...(isEdit && activityConfigId ? { activityConfigId } : {}),
          ...(isEdit && activityIdFromConfig ? { activityId: activityIdFromConfig } : {}),
          allowedToDate: format(toDate, "yyyy-MM-dd"),
          timezone: selectedTimezone,
          visibilityWindows
        }
      };

      if (isEdit && activityId) {
        await activityService.updateActivity(Number(user.id), activityId, payload, token);
        toast.success("Activity updated successfully");
        navigate("/admin/activity");
      } else {
        await activityService.createActivity(Number(user.id), payload, token);
        toast.success("Activity created successfully");
        navigate("/admin/activity");
      }
    } catch (error) {
      console.error("Error saving activity:", error);
      toast.error(error instanceof Error ? error.message : "Failed to save activity");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to convert 12-hour time to 24-hour format
  const convertTo24Hour = (time12h: string): string => {
    const [time, period] = time12h.split(' ');
    let [hours, minutes] = time.split(':');
    let hour = parseInt(hours);
    
    if (period === 'PM' && hour !== 12) {
      hour += 12;
    } else if (period === 'AM' && hour === 12) {
      hour = 0;
    }
    
    return `${hour.toString().padStart(2, '0')}:${minutes}`;
  };

  const onCancel = () => {
    navigate("/admin/activity");
  };

  // Helper function to check if any enabled day has incomplete slots
  const hasIncompleteSlotsInEnabledDays = (): { hasIncomplete: boolean; dayName?: string } => {
    for (const [dayName, dayData] of Object.entries(appointmentSlots)) {
      if (dayData.enabled) {
        // Check if any slot in this enabled day is incomplete
        for (const slot of dayData.slots) {
          const isIncomplete = 
            !slot.fromTime || 
            !slot.toTime || 
            !!slot.error;
          
          if (isIncomplete) {
            return { hasIncomplete: true, dayName };
          }
        }
      }
    }
    return { hasIncomplete: false };
  };

  // Functions to manage appointment slots
  const toggleDay = (day: string) => {
    const isCurrentlyEnabled = appointmentSlots[day].enabled;
    
    // If trying to enable a new day, first check if other enabled days have incomplete slots
    if (!isCurrentlyEnabled) {
      const validation = hasIncompleteSlotsInEnabledDays();
      if (validation.hasIncomplete) {
        toast.error(
          `Please complete all slot details for ${validation.dayName} first, or uncheck the day before enabling another day.`,
          { duration: 5000 }
        );
        return;
      }
      
      // Auto-expand the day when enabling it so user can fill slot details
      setCollapsedDays(prev => ({
        ...prev,
        [day]: false
      }));
    }
    
    setAppointmentSlots(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        enabled: !prev[day].enabled,
      }
    }));
  };

  const toggleDayCollapse = (day: string) => {
    // Only allow collapse toggle if the day is enabled
    if (!appointmentSlots[day]?.enabled) return;
    
    setCollapsedDays(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };

  const addSlot = (day: string) => {
    // Check if current day has any incomplete slots before allowing to add more
    const currentDaySlots = appointmentSlots[day].slots;
    const hasIncompleteSlot = currentDaySlots.some(slot => 
      !slot.fromTime || 
      !slot.toTime || 
      !!slot.error
    );
    
    if (hasIncompleteSlot) {
      toast.error(
        `Please complete all existing slot details for ${day} before adding a new slot.`,
        { duration: 5000 }
      );
      return;
    }
    
    // Also check if other enabled days have incomplete slots
    const validation = hasIncompleteSlotsInEnabledDays();
    if (validation.hasIncomplete && validation.dayName !== day) {
      toast.error(
        `Please complete all slot details for ${validation.dayName} first, or uncheck the day before adding slots to another day.`,
        { duration: 5000 }
      );
      return;
    }
    
    setAppointmentSlots(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: [
          ...prev[day].slots,
          { 
            id: Math.random().toString(), 
            fromTime: "", 
            toTime: "",
            timezone: detectedTimezone,
            slotDurationMinutes: ""
          }
        ]
      }
    }));
  };

  const removeSlot = (day: string, slotId: string) => {
    const dayData = appointmentSlots[day];
    
    // If this is the last slot, show confirmation dialog to remove all slots for the day
    if (dayData.slots.length === 1) {
      setShowRemoveAllSlotsDialog({ day, slotId });
      return;
    }
    
    const slot = appointmentSlots[day].slots.find(s => s.id === slotId);
    
    // If slot has mapper IDs (existing slot from backend) and we're in edit mode, show confirmation
    if (isEdit && slot?.mapperIds && slot.mapperIds.length > 0) {
      setSlotToDelete({ day, slot });
    } else {
      // Just remove from UI state (new slot not saved yet)
      setAppointmentSlots(prev => {
        const updatedDay = {
          ...prev[day],
          slots: prev[day].slots.filter(s => s.id !== slotId)
        };
        
        // Revalidate remaining slots to clear any overlap errors
        updatedDay.slots = updatedDay.slots.map(slot => {
          if (slot.fromTime && slot.toTime) {
            const basicError = validateTimeSlot(slot.fromTime, slot.toTime);
            if (!basicError) {
              // Check overlaps in the updated list
              const otherSlots = updatedDay.slots.filter(
                s => s.id !== slot.id && s.fromTime && s.toTime
              );
              let overlapError: string | null = null;
              for (const otherSlot of otherSlots) {
                const currentFromMinutes = parseTime(slot.fromTime);
                const currentToMinutes = parseTime(slot.toTime);
                const slotFromMinutes = parseTime(otherSlot.fromTime);
                const slotToMinutes = parseTime(otherSlot.toTime);

                const overlaps = (
                  (currentFromMinutes >= slotFromMinutes && currentFromMinutes < slotToMinutes) ||
                  (currentToMinutes > slotFromMinutes && currentToMinutes <= slotToMinutes) ||
                  (currentFromMinutes <= slotFromMinutes && currentToMinutes >= slotToMinutes)
                );

                if (overlaps) {
                  overlapError = `Time slot overlaps with existing slot: ${otherSlot.fromTime} - ${otherSlot.toTime}`;
                  break;
                }
              }
              slot.error = overlapError || undefined;
            } else {
              slot.error = basicError;
            }
          }
          return slot;
        });

        return {
          ...prev,
          [day]: updatedDay
        };
      });
    }
  };

  const handleRemoveAllSlotsConfirm = async () => {
    if (!showRemoveAllSlotsDialog) return;

    const { day, slotId } = showRemoveAllSlotsDialog;
    const slot = appointmentSlots[day].slots.find(s => s.id === slotId);

    // If in edit mode and slot has mapper IDs, delete from backend
    if (isEdit && slot && slot.mapperIds && slot.mapperIds.length > 0 && token && user) {
      setIsDeletingSlot(true);
      try {
        await activityService.deleteAppointmentSlots(
          Number(user.id),
          slot.mapperIds,
          token
        );
        toast.success("All appointment slots for this day deleted successfully");
      } catch (error: any) {
        toast.error(error.message || "Failed to delete appointment slots");
        setShowRemoveAllSlotsDialog(null);
        setIsDeletingSlot(false);
        return;
      } finally {
        setIsDeletingSlot(false);
      }
    }

    // Remove all slots and disable the day
    setAppointmentSlots(prev => ({
      ...prev,
      [day]: {
        enabled: false,
        slots: [{ id: `${day}-1`, fromTime: "", toTime: "", timezone: selectedTimezone, slotDurationMinutes: "" }]
      }
    }));

    setShowRemoveAllSlotsDialog(null);
    toast.success(`All slots removed for ${day}`);
  };

  const handleDeleteSlotConfirm = async () => {
    if (!slotToDelete || !token || !user) return;

    const { day, slot } = slotToDelete;
    
    if (!slot.mapperIds || slot.mapperIds.length === 0) {
      toast.error("No appointment slots to delete");
      setSlotToDelete(null);
      return;
    }

    setIsDeletingSlot(true);
    try {
      await activityService.deleteAppointmentSlots(
        Number(user.id),
        slot.mapperIds,
        token
      );

      toast.success("Appointment slots deleted successfully");

      // Remove the slot from UI state
      setAppointmentSlots(prev => ({
        ...prev,
        [day]: {
          ...prev[day],
          slots: prev[day].slots.filter(s => s.id !== slot.id)
        }
      }));

      setSlotToDelete(null);
    } catch (error: any) {
      toast.error(error.message || "Failed to delete appointment slots");
    } finally {
      setIsDeletingSlot(false);
    }
  };

  const duplicateSlot = (day: string, slotId: string) => {
    const slotToDuplicate = appointmentSlots[day].slots.find(slot => slot.id === slotId);
    if (slotToDuplicate) {
      setAppointmentSlots(prev => ({
        ...prev,
        [day]: {
          ...prev[day],
          slots: [
            ...prev[day].slots,
            { 
              ...slotToDuplicate, 
              id: Math.random().toString(),
              error: slotToDuplicate.fromTime && slotToDuplicate.toTime 
                ? "Duplicate slot - please adjust times to avoid overlap" 
                : undefined
            }
          ]
        }
      }));
    }
  };

  // Copy all slots from a day to all other days (only for create mode)
  const copySlotsToAllDays = (sourceDay: string) => {
    const validSlots = appointmentSlots[sourceDay].slots.filter(
      slot => slot.fromTime && slot.toTime && !slot.error
    );
    
    if (validSlots.length === 0) {
      toast.error("Please add at least one complete time slot before copying to all days");
      return;
    }

    // Get all visible days except the source day
    const otherDays = sortedDays.filter(day => day !== sourceDay);
    
    if (otherDays.length === 0) {
      toast.info("No other days available in the selected date range");
      return;
    }

    let totalCopied = 0;
    let totalSkipped = 0;

    setAppointmentSlots(prev => {
      const updated = { ...prev };
      
      otherDays.forEach(day => {
        validSlots.forEach(sourceSlot => {
          // Check if a slot with the same time already exists
          const existingSlot = updated[day].slots.find(
            slot => slot.fromTime === sourceSlot.fromTime && slot.toTime === sourceSlot.toTime
          );
          
          if (existingSlot) {
            totalSkipped++;
            return;
          }

          // Enable the day if not already enabled and add slot
          if (!updated[day].enabled) {
            updated[day] = {
              enabled: true,
              slots: [{
                id: Math.random().toString(),
                fromTime: sourceSlot.fromTime,
                toTime: sourceSlot.toTime,
                timezone: sourceSlot.timezone,
                slotDurationMinutes: sourceSlot.slotDurationMinutes,
                visibilityWindowId: null
              }]
            };
          } else {
            updated[day] = {
              ...updated[day],
              slots: [
                ...updated[day].slots,
                {
                  id: Math.random().toString(),
                  fromTime: sourceSlot.fromTime,
                  toTime: sourceSlot.toTime,
                  timezone: sourceSlot.timezone,
                  slotDurationMinutes: sourceSlot.slotDurationMinutes,
                  visibilityWindowId: null
                }
              ]
            };
          }
          totalCopied++;
        });
      });

      return updated;
    });

    if (totalCopied > 0) {
      toast.success(`${totalCopied} slot(s) copied to other days${totalSkipped > 0 ? `, ${totalSkipped} skipped (duplicates)` : ''}`);
    } else if (totalSkipped > 0) {
      toast.info(`All days already have these time slots`);
    }
  };

  // Helper to parse time string to minutes
  const parseTime = (timeStr: string) => {
    if (!timeStr) return 0;
    const [time, period] = timeStr.split(' ');
    const [hours, minutes] = time.split(':');
    let hour = parseInt(hours);
    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
    return hour * 60 + parseInt(minutes);
  };

  // Time validation helper
  const validateTimeSlot = (fromTime: string, toTime: string): string | null => {
    if (!fromTime || !toTime) return "Both From and To times are required";

    // Validate time format has valid hour and minute
    if (!isValidTimeString(fromTime)) return "From time must have valid hour and minutes";
    if (!isValidTimeString(toTime)) return "To time must have valid hour and minutes";

    const fromMinutes = parseTime(fromTime);
    const toMinutes = parseTime(toTime);
    
    if (toMinutes <= fromMinutes) {
      return "To time must be after From time";
    }
    
    return null;
  };

  // Check for overlapping slots
  const checkSlotOverlap = (day: string, currentSlotId: string, fromTime: string, toTime: string, slotsToCheck?: TimeSlot[]): string | null => {
    if (!fromTime || !toTime) return null;

    const currentFromMinutes = parseTime(fromTime);
    const currentToMinutes = parseTime(toTime);

    // Use provided slots list or fall back to current state
    const allSlots = slotsToCheck || appointmentSlots[day].slots;
    
    // Check against all other slots in the same day (exclude current slot being edited)
    const otherSlots = allSlots.filter(
      slot => slot.id !== currentSlotId && slot.fromTime && slot.toTime
    );

    for (const slot of otherSlots) {
      const slotFromMinutes = parseTime(slot.fromTime);
      const slotToMinutes = parseTime(slot.toTime);

      // Check if slots overlap
      const overlaps = (
        (currentFromMinutes >= slotFromMinutes && currentFromMinutes < slotToMinutes) ||
        (currentToMinutes > slotFromMinutes && currentToMinutes <= slotToMinutes) ||
        (currentFromMinutes <= slotFromMinutes && currentToMinutes >= slotToMinutes)
      );

      if (overlaps) {
        return `Time slot overlaps with existing slot: ${slot.fromTime} - ${slot.toTime}`;
      }
    }

    return null;
  };

  const updateSlot = (day: string, slotId: string, field: 'fromTime' | 'toTime' | 'timezone', value: string) => {
    setAppointmentSlots(prev => {
      // Create updated slots list first for accurate overlap checking
      const updatedSlots = prev[day].slots.map(slot => {
        if (slot.id === slotId) {
          return { ...slot, [field]: value };
        }
        return slot;
      });

      // Now validate with the updated slots list
      const finalSlots = updatedSlots.map(slot => {
        if (slot.id === slotId) {
          const updatedSlot = { ...slot };
          // Validate time if fromTime or toTime is updated
          if (field === 'fromTime' || field === 'toTime') {
            // First check if To time is after From time
            const basicError = validateTimeSlot(updatedSlot.fromTime, updatedSlot.toTime);
            if (basicError) {
              updatedSlot.error = basicError;
            } else {
              // Then check for overlaps with other slots using the updated list
              const overlapError = checkSlotOverlap(day, slotId, updatedSlot.fromTime, updatedSlot.toTime, updatedSlots);
              updatedSlot.error = overlapError || undefined;
            }
          }
          return updatedSlot;
        }
        return slot;
      });

      return {
        ...prev,
        [day]: {
          ...prev[day],
          slots: finalSlots
        }
      };
    });
  };

  // Filter days based on date range
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const visibleDays = daysInRange.length > 0 ? daysInRange : [];
  
  // Sort visible days: active days first, then inactive, maintain week order
  const sortedDays = [...visibleDays].sort((a, b) => {
    if (appointmentSlots[a].enabled && !appointmentSlots[b].enabled) return -1;
    if (!appointmentSlots[a].enabled && appointmentSlots[b].enabled) return 1;
    return daysOfWeek.indexOf(a) - daysOfWeek.indexOf(b);
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">
          {isEdit ? "Edit Activity" : isDuplicate ? "Duplicate Activity" : "Add New Activity"}
        </h1>
        <p className="text-muted-foreground mt-2">
          {isEdit ? "Update activity details" : isDuplicate ? "Create a copy of the activity" : "Create a new activity"}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Activity Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* First Line: Activity Name, Patient Group, Workflow */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="activityName"
                  render={({ field }) => (
                    <FormItem>
                      <Label htmlFor="activityName">Activity Name *</Label>
                      <FormControl>
                        <Input
                          id="activityName"
                          placeholder="Enter activity name"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="patientGroup"
                  render={({ field }) => (
                    <FormItem>
                      <Label htmlFor="patientGroup">Patient Group *</Label>
                      {patientGroupsLoading ? (
                        <Skeleton className="w-full h-10" />
                      ) : patientGroupsError ? (
                        <span className="text-sm text-destructive">Error loading patient groups</span>
                      ) : (
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                role="combobox"
                                className={cn(
                                  "w-full justify-between",
                                  !field.value && "text-muted-foreground"
                                )}
                              >
                                {field.value
                                  ? patientGroups.find((group) => group.patient_group_id.toString() === field.value)?.group_name
                                  : "Select patient group"}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-full p-0 z-50 bg-background border shadow-md">
                            <Command className="bg-background">
                              <CommandInput placeholder="Search patient groups..." className="border-none" />
                              <CommandList className="max-h-60">
                                <CommandEmpty className="p-4 text-sm text-muted-foreground">No patient group found.</CommandEmpty>
                                <CommandGroup>
                                  {patientGroups.map((group) => (
                                    <CommandItem
                                      value={group.group_name}
                                      key={group.patient_group_id}
                                      onSelect={() => {
                                        field.onChange(group.patient_group_id.toString());
                                      }}
                                      className="cursor-pointer hover:bg-muted"
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          group.patient_group_id.toString() === field.value ? "opacity-100" : "opacity-0"
                                        )}
                                      />
                                      <div className="flex flex-col">
                                        <span>{group.group_name}</span>
                                        <span className="text-xs text-muted-foreground">
                                          {group.patient_count} patient{group.patient_count !== 1 ? 's' : ''}
                                        </span>
                                      </div>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="initialWorkflow"
                  render={({ field }) => (
                    <FormItem>
                      <Label htmlFor="initialWorkflow">Workflow *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              role="combobox"
                              disabled={workflowsLoading}
                              className={cn(
                                "w-full justify-between",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {workflowsLoading ? (
                                "Loading..."
                              ) : field.value ? (
                                workflows.find((workflow) => workflow.workflow_id.toString() === field.value)?.workflow_name
                              ) : (
                                "Select workflow"
                              )}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0 z-50 bg-background border shadow-md">
                          <Command className="bg-background">
                            <CommandInput placeholder="Search workflows..." className="border-none" />
                            <CommandList className="max-h-60">
                              <CommandEmpty className="p-4 text-sm text-muted-foreground">
                                {workflowsError ? "Error loading workflows" : "No workflow found."}
                              </CommandEmpty>
                              <CommandGroup>
                                {workflows.map((workflow) => (
                                  <CommandItem
                                    value={workflow.workflow_name}
                                    key={workflow.workflow_id}
                                    onSelect={() => {
                                      field.onChange(workflow.workflow_id.toString());
                                    }}
                                    className="cursor-pointer hover:bg-muted"
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        workflow.workflow_id.toString() === field.value ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    {workflow.workflow_name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Appointment Slots */}
          <Card className="overflow-hidden">
            <CardHeader className="border-b bg-background">
              <div className="space-y-4">
                <CardTitle className="flex items-center justify-between text-lg">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-primary" />
                    Activity Schedule
                  </div>
                  <Badge variant="secondary">
                    {Object.values(appointmentSlots).filter(day => day.enabled).length} Active Days
                  </Badge>
                </CardTitle>
                
                {/* Date and Timezone Selection */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* From Date */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">From Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal h-10",
                            !fromDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {fromDate ? format(fromDate, "MM/dd/yyyy") : <span>Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={fromDate}
                          onSelect={(date) => {
                            setFromDate(date);
                            setDateError("");
                            // Validate if toDate is set
                            if (toDate && date && date > toDate) {
                              setDateError("From date must be before or equal to end date");
                            }
                          }}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* End Date */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">End Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal h-10",
                            !toDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {toDate ? format(toDate, "MM/dd/yyyy") : <span>Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={toDate}
                          onSelect={(date) => {
                            setToDate(date);
                            setDateError("");
                            // Validate if fromDate is set
                            if (fromDate && date && date < fromDate) {
                              setDateError("End date must be after or equal to from date");
                            }
                          }}
                          disabled={(date) => {
                            const today = new Date(new Date().setHours(0, 0, 0, 0));
                            if (fromDate) {
                              return date < fromDate || date < today;
                            }
                            return date < today;
                          }}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Timezone */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Timezone</Label>
                    {timezonesLoading ? (
                      <Skeleton className="w-full h-10" />
                    ) : timezonesError ? (
                      <span className="text-sm text-destructive">Error loading timezones</span>
                    ) : (
                      <Select value={selectedTimezone} onValueChange={setSelectedTimezone}>
                        <SelectTrigger className="w-full h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {timezones.map((tz) => (
                            <SelectItem key={tz.timezone} value={tz.timezone}>
                              {tz.timezone}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
                
                {/* Date Validation Error */}
                {dateError && (
                  <div className="text-sm text-destructive bg-destructive/10 p-2 rounded border border-destructive/20">
                    {dateError}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <TooltipProvider>
                {sortedDays.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <p>Please select From Date and End Date to see available days</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {sortedDays.map((day) => {
                    const dayData = appointmentSlots[day];
                    const isCollapsed = collapsedDays[day];
                    
                    return (
                      <Collapsible
                        key={day}
                        open={!isCollapsed}
                        onOpenChange={() => toggleDayCollapse(day)}
                      >
                        <div className={cn(
                          "transition-colors duration-200",
                          dayData.enabled ? "bg-muted/30" : "bg-background"
                        )}>
                          <CollapsibleTrigger asChild>
                            <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors">
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-3">
                                  <input
                                    type="checkbox"
                                    id={day}
                                    checked={dayData.enabled}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      toggleDay(day);
                                    }}
                                    className="h-5 w-5 text-primary rounded border-2 border-border focus:ring-2 focus:ring-primary focus:ring-offset-0 transition-all"
                                  />
                                  <Label 
                                    htmlFor={day} 
                                    className="font-semibold text-base cursor-pointer select-none min-w-[60px]"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {day}
                                  </Label>
                                </div>
                                
                                {dayData.enabled && (
                                  <div className="flex gap-2 flex-wrap">
                                    {dayData.slots
                                      .filter(slot => slot.fromTime && slot.toTime)
                                      .map((slot) => (
                                        <Badge 
                                          key={slot.id} 
                                          variant="outline"
                                          className="bg-background border-muted-foreground/20 text-foreground font-medium px-3 py-1"
                                        >
                                          <Clock className="h-3 w-3 mr-1" />
                                          {slot.fromTime} - {slot.toTime}
                                        </Badge>
                                      ))
                                    }
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex items-center gap-2">
                                {dayData.enabled && (
                                  <>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            addSlot(day);
                                          }}
                                          className="h-8 w-8 p-0 hover:bg-muted hover:text-foreground"
                                        >
                                          <Plus className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Add new slot</TooltipContent>
                                    </Tooltip>
                                    
                                    {/* Copy to All Days button - only for Monday in create mode */}
                                    {!isEdit && day === 'Monday' && dayData.slots.some(slot => slot.fromTime && slot.toTime && !slot.error) && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              copySlotsToAllDays('Monday');
                                            }}
                                            className="h-8 px-2 hover:bg-primary/10 hover:text-primary"
                                          >
                                            <CopyPlus className="h-4 w-4 mr-1" />
                                            <span className="text-xs">Copy to All Days</span>
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Copy all Monday slots to other days</TooltipContent>
                                      </Tooltip>
                                    )}
                                    
                                    <Badge variant="secondary" className="bg-muted text-foreground">
                                      {dayData.slots.filter(slot => slot.fromTime && slot.toTime).length} slots
                                    </Badge>
                                  </>
                                )}
                                
                                {dayData.enabled && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                  >
                                    {isCollapsed ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronUp className="h-4 w-4" />
                                    )}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CollapsibleTrigger>
                          
                          {dayData.enabled && (
                            <CollapsibleContent>
                              <div className="px-4 pb-4 space-y-3 bg-background">
                                <div className="grid gap-3">
                                  {dayData.slots.map((slot) => (
                                    <div 
                                      key={slot.id} 
                                      className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                                    >
                                      <div className="flex items-center gap-3 flex-1">
                                        <Clock className="h-4 w-4 text-muted-foreground mt-1" />
                                        
                                        <div className="flex items-center gap-2">
                                          {/* From Time */}
                                          <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">From Time *</Label>
                                            <div className="flex items-center gap-1">
                                              <Select
                                                value={parseTimeString(slot.fromTime).hour}
                                                onValueChange={(hour) => {
                                                  const { minute, period } = parseTimeString(slot.fromTime);
                                                  const newTime = constructTimeString(hour, minute || '00', period || 'AM');
                                                  updateSlot(day, slot.id, 'fromTime', newTime);
                                                }}
                                              >
                                                <SelectTrigger className="w-16 bg-background">
                                                  <SelectValue placeholder="HH" />
                                                </SelectTrigger>
                                                <SelectContent className="max-h-60">
                                                  {hourOptions.map((hour) => (
                                                    <SelectItem key={hour.value} value={hour.value}>
                                                      {hour.label}
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                              <span className="text-muted-foreground">:</span>
                                              <Select
                                                value={parseTimeString(slot.fromTime).minute}
                                                onValueChange={(minute) => {
                                                  const { hour, period } = parseTimeString(slot.fromTime);
                                                  const newTime = constructTimeString(hour || '12', minute, period || 'AM');
                                                  updateSlot(day, slot.id, 'fromTime', newTime);
                                                }}
                                              >
                                                <SelectTrigger className="w-16 bg-background">
                                                  <SelectValue placeholder="MM" />
                                                </SelectTrigger>
                                                <SelectContent className="max-h-60">
                                                  {minuteOptions.map((minute) => (
                                                    <SelectItem key={minute.value} value={minute.value}>
                                                      {minute.label}
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                              <Select
                                                value={parseTimeString(slot.fromTime).period}
                                                onValueChange={(period) => {
                                                  const { hour, minute } = parseTimeString(slot.fromTime);
                                                  const newTime = constructTimeString(hour || '12', minute || '00', period);
                                                  updateSlot(day, slot.id, 'fromTime', newTime);
                                                }}
                                              >
                                                <SelectTrigger className="w-16 bg-background">
                                                  <SelectValue placeholder="AM" />
                                                </SelectTrigger>
                                                <SelectContent className="max-h-60">
                                                  {periodOptions.map((period) => (
                                                    <SelectItem key={period.value} value={period.value}>
                                                      {period.label}
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            </div>
                                          </div>
                                          
                                          <span className="text-muted-foreground mt-4">→</span>
                                          
                                          {/* To Time */}
                                          <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">To Time *</Label>
                                            <div className="flex items-center gap-1">
                                              <Select
                                                value={parseTimeString(slot.toTime).hour}
                                                onValueChange={(hour) => {
                                                  const { minute, period } = parseTimeString(slot.toTime);
                                                  const newTime = constructTimeString(hour, minute || '00', period || 'AM');
                                                  updateSlot(day, slot.id, 'toTime', newTime);
                                                }}
                                              >
                                                <SelectTrigger className="w-16 bg-background">
                                                  <SelectValue placeholder="HH" />
                                                </SelectTrigger>
                                                <SelectContent className="max-h-60">
                                                  {hourOptions.map((hour) => (
                                                    <SelectItem key={hour.value} value={hour.value}>
                                                      {hour.label}
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                              <span className="text-muted-foreground">:</span>
                                              <Select
                                                value={parseTimeString(slot.toTime).minute}
                                                onValueChange={(minute) => {
                                                  const { hour, period } = parseTimeString(slot.toTime);
                                                  const newTime = constructTimeString(hour || '12', minute, period || 'AM');
                                                  updateSlot(day, slot.id, 'toTime', newTime);
                                                }}
                                              >
                                                <SelectTrigger className="w-16 bg-background">
                                                  <SelectValue placeholder="MM" />
                                                </SelectTrigger>
                                                <SelectContent className="max-h-60">
                                                  {minuteOptions.map((minute) => (
                                                    <SelectItem key={minute.value} value={minute.value}>
                                                      {minute.label}
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                              <Select
                                                value={parseTimeString(slot.toTime).period}
                                                onValueChange={(period) => {
                                                  const { hour, minute } = parseTimeString(slot.toTime);
                                                  const newTime = constructTimeString(hour || '12', minute || '00', period);
                                                  updateSlot(day, slot.id, 'toTime', newTime);
                                                }}
                                              >
                                                <SelectTrigger className="w-16 bg-background">
                                                  <SelectValue placeholder="AM" />
                                                </SelectTrigger>
                                                <SelectContent className="max-h-60">
                                                  {periodOptions.map((period) => (
                                                    <SelectItem key={period.value} value={period.value}>
                                                      {period.label}
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            </div>
                                          </div>
                                          
                                         </div>
                                      </div>
                                      
                                      <div className="flex gap-1 mt-6">
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Button
                                              type="button"
                                              variant="outline"
                                              size="sm"
                                              onClick={() => duplicateSlot(day, slot.id)}
                                              className="h-8 w-8 p-0"
                                            >
                                              <Copy className="h-3 w-3" />
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent>Duplicate slot</TooltipContent>
                                        </Tooltip>
                                        
                                        
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Button
                                              type="button"
                                              variant="outline"
                                              size="sm"
                                              onClick={() => removeSlot(day, slot.id)}
                                              disabled={isDeletingSlot}
                                              className="h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                                            >
                                              <Trash2 className="h-3 w-3" />
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            {dayData.slots.length === 1 
                                              ? "Remove all slots for this day" 
                                              : "Remove slot"}
                                          </TooltipContent>
                                        </Tooltip>
                                      </div>
                                    </div>
                                  ))}
                                  
                                  {/* Error messages for invalid slots */}
                                  {dayData.slots.some(slot => slot.error) && (
                                    <div className="space-y-1">
                                      {dayData.slots
                                        .filter(slot => slot.error)
                                        .map(slot => (
                                          <div key={slot.id} className="text-sm text-destructive bg-destructive/10 p-2 rounded border border-destructive/20">
                                            {slot.error}
                                          </div>
                                        ))
                                      }
                                    </div>
                                  )}
                                </div>
                                
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => addSlot(day)}
                                  className="w-full mt-3 border-dashed"
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  Add Another Slot
                                </Button>
                              </div>
                            </CollapsibleContent>
                          )}
                        </div>
                      </Collapsible>
                      );
                    })}
                  </div>
                )}
              </TooltipProvider>
            </CardContent>
          </Card>

          {/* Form Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !isFormValid()}>
              {isSubmitting ? "Saving..." : isEdit ? "Update Activity" : isDuplicate ? "Create Duplicate" : "Save Activity"}
            </Button>
          </div>
        </form>
      </Form>

      {/* Delete Appointment Slot Confirmation Dialog */}
      {slotToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-2">Delete Appointment Slot</h3>
            <p className="text-muted-foreground mb-4">
              Are you sure you want to delete this appointment slot for {slotToDelete.day}? 
              This will delete {slotToDelete.slot.mapperIds?.length || 0} appointment(s).
            </p>
            <div className="flex justify-end gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setSlotToDelete(null)}
                disabled={isDeletingSlot}
              >
                Cancel
              </Button>
              <Button 
                type="button" 
                variant="destructive"
                onClick={handleDeleteSlotConfirm}
                disabled={isDeletingSlot}
              >
                {isDeletingSlot ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Remove All Slots Confirmation Dialog */}
      {showRemoveAllSlotsDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-2">Remove All Slots for {showRemoveAllSlotsDialog.day}</h3>
            <p className="text-muted-foreground mb-4">
              This is the last slot for {showRemoveAllSlotsDialog.day}. Removing it will disable all appointments for this day. Are you sure you want to continue?
            </p>
            <div className="flex justify-end gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowRemoveAllSlotsDialog(null)}
                disabled={isDeletingSlot}
              >
                Cancel
              </Button>
              <Button 
                type="button" 
                variant="destructive"
                onClick={handleRemoveAllSlotsConfirm}
                disabled={isDeletingSlot}
              >
                {isDeletingSlot ? "Removing..." : "Remove All Slots"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityForm;